import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, AlertCircle, Lock, Sparkles } from "lucide-react";
import { GuideDownloadButton } from "@/components/GuideDownloadButton";
import { PageShell, SubpageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { formatDate } from "@/data/posts";
import { useNiches, usePosts } from "@/hooks/useContent";
import { useSeo } from "@/hooks/useSeo";

const GuideCard = ({ guide, i, niche }) => (
  <Reveal delay={i * 0.06}>
    <article
      data-testid={`guide-card-${i}`}
      className="h-full flex flex-col rounded-3xl bg-white border border-[#103080]/10 p-7 sm:p-8 lift-shadow"
    >
      <span className="text-[11px] uppercase tracking-[0.25em] font-medium" style={{ color: niche.accent }}>Guide {String(i + 1).padStart(2, "0")}</span>
      <h3 className="font-serif font-semibold text-2xl text-[#103080] leading-tight mt-3">{guide.title}</h3>
      <p className="text-sm text-[#0F172A]/60 mt-2 leading-relaxed">{guide.summary}</p>
      <p className="mt-6 text-[11px] uppercase tracking-[0.2em] font-medium text-[#0F172A]/45">Inside the guide</p>
      <ul className="mt-3 space-y-3" data-testid={`guide-teaser-${i}`}>
        {(guide.teaser?.length ? guide.teaser : [guide.summary]).map((pt) => (
          <li key={pt} className="flex gap-3 text-sm sm:text-[15px] text-[#0F172A]/75 leading-relaxed">
            <Sparkles size={17} className="shrink-0 mt-0.5" style={{ color: niche.accent }} />
            <span>{pt}</span>
          </li>
        ))}
        <li className="flex gap-3 text-sm text-[#0F172A]/50 italic">
          <Lock size={16} className="shrink-0 mt-0.5 text-[#0F172A]/35" />
          <span>+ {guide.points.length} practical points and a tick-box action plan in the full PDF</span>
        </li>
      </ul>
      <div className="mt-auto">
        <GuideDownloadButton niche={niche} guide={guide} index={i} accent={niche.accent} />
      </div>
    </article>
  </Reveal>
);

export default function NichePage() {
  const { slug } = useParams();
  const NICHES = useNiches();
  const POSTS = usePosts();
  const niche = NICHES.find((n) => n.slug === slug);

  useSeo({
    title: niche ? `Accountants for ${niche.name} in Bristol & Bath | Rosevale Accountants` : "Rosevale Accountants",
    description: niche ? niche.intro.slice(0, 155) : "",
    path: `/toolkit/${slug}`,
  });

  if (!niche) return NICHES.loaded ? <Navigate to="/#toolkit" replace /> : null;

  const related = (niche.relatedPosts || []).map((s) => POSTS.find((p) => p.slug === s)).filter(Boolean);
  const others = NICHES.filter((n) => n.slug !== niche.slug);
  const Icon = niche.icon;

  return (
    <PageShell testid={`niche-page-${niche.slug}`}>
      <SubpageHero
        eyebrow={`Business Toolkit · ${niche.name}`}
        title={niche.heroLine}
        lead={niche.intro}
        crumbs={[{ label: "Business Toolkit", to: "/#toolkit" }, { label: niche.name }]}
      >
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <a
            href="https://calendly.com/abbie-browne"
            target="_blank"
            rel="noreferrer"
            data-testid="niche-book-call-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[#103080] text-white text-sm font-medium px-7 py-3.5 hover:bg-[#0090D0] transition-colors duration-300"
          >
            Book a free discovery call
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <span className="inline-flex items-center gap-2 text-sm text-[#0F172A]/55">
            <Icon size={16} style={{ color: niche.accent }} /> {niche.short}
          </span>
        </div>
      </SubpageHero>

      <section className="py-20 sm:py-28 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-medium mb-5">Where it goes wrong</p>
            <h2 className="font-serif font-semibold tracking-tight text-[#103080] text-3xl sm:text-4xl leading-tight">{niche.painsTitle}</h2>
            <p className="mt-5 text-[#0F172A]/65 leading-relaxed">
              None of these are hard to fix once you know they're there. They're the first things we look at with a new {niche.name.toLowerCase()} client.
            </p>
          </Reveal>
          <div className="lg:col-span-7 space-y-4">
            {niche.pains.map((p, i) => (
              <Reveal key={p} delay={i * 0.06}>
                <div className="flex gap-4 rounded-2xl bg-white border border-[#103080]/10 p-5 sm:p-6" data-testid={`pain-point-${i}`}>
                  <AlertCircle size={20} className="shrink-0 mt-0.5 text-[#0090D0]" />
                  <p className="text-sm sm:text-base text-[#0F172A]/75 leading-relaxed">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-[#F5F2EB] border-y border-[#103080]/10" data-testid="niche-guides">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-medium mb-5">Free downloadable guides</p>
            <h2 className="font-serif font-semibold tracking-tight text-[#103080] text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-2xl">
              The essentials, written for {niche.name.toLowerCase()} businesses.
            </h2>
            <p className="mt-5 mb-12 sm:mb-16 text-[#0F172A]/65 leading-relaxed max-w-xl">
              Each guide is a short, branded PDF you can keep and share with your team. Pop in your email and it downloads straight away.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {niche.guides.map((g, i) => <GuideCard key={g.title} guide={g} i={i} niche={niche} />)}
          </div>
          <p className="mt-8 text-xs text-[#0F172A]/45 max-w-2xl">
            These guides are general information based on current UK rules and aren't a substitute for advice on your own circumstances. Rates and thresholds change — book a call and we'll check your position.
          </p>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-20 sm:py-28 bg-[#FDFBF7]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <Reveal>
              <div className="flex items-end justify-between gap-6 mb-10">
                <h2 className="font-serif font-semibold tracking-tight text-[#103080] text-3xl sm:text-4xl">Related reading</h2>
                <Link to="/blog" className="group hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#103080] hover:text-[#0090D0] transition-colors duration-300">
                  All posts <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {related.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`related-post-${p.slug}`} className="group flex gap-5 rounded-2xl bg-white border border-[#103080]/10 p-4 lift-shadow">
                  <img src={p.image} alt="" className="w-28 h-28 sm:w-36 sm:h-36 object-cover rounded-xl shrink-0" />
                  <div className="min-w-0 py-1">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-[#0F172A]/45">{formatDate(p.date)} · {p.readTime}</p>
                    <h3 className="font-serif font-semibold text-[#103080] text-lg leading-snug mt-2 group-hover:text-[#0090D0] transition-colors duration-300">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-14 bg-[#F5F2EB] border-t border-[#103080]/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#0F172A]/45 font-medium mb-5">Other specialisms</p>
          <div className="flex flex-wrap gap-3">
            {others.map((n) => (
              <Link key={n.slug} to={`/toolkit/${n.slug}`} data-testid={`other-niche-${n.slug}`} className="inline-flex items-center gap-2 rounded-full bg-white border border-[#103080]/10 px-5 py-2.5 text-sm font-medium text-[#103080] hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-300">
                <n.icon size={15} /> {n.name}
              </Link>
            ))}
            <Link to="/making-tax-digital" className="inline-flex items-center gap-2 rounded-full bg-[#103080] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#0090D0] transition-colors duration-300">
              Making Tax Digital <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
